import { MATRIX_LOCALES } from '@web-studio/core-fixtures';
import {
  getColorSetsForTheme,
  SPACING_ENUM_KEY_VALUES,
  THEME_REGISTRY,
  type RegistryBlockType,
  type SpacingEnumKey,
} from '@web-studio/shared-schemas';
import type { MatrixLocale } from '@web-studio/core-fixtures';
import {
  listLabArrangements,
  listLabBlockOptions,
  listLabLooks,
  parseLabLook,
  resolveLabArrangement,
  type LabArrangementOption,
} from '@/lib/blockAxes';
import type { MatrixViewState } from '@/lib/matrixState';

interface MatrixToolbarProps {
  state: MatrixViewState;
  onChange: (patch: Partial<MatrixViewState>) => void;
  showBlockAxes?: boolean;
}

function patchFromArrangement(
  blockType: RegistryBlockType,
  arrangement: LabArrangementOption,
  look: string | null,
): Partial<MatrixViewState> {
  return {
    blockType,
    arrangementId: arrangement.id,
    presentation: arrangement.presentation,
    componentType: arrangement.componentType,
    fixtureId: arrangement.fixtureId,
    look: parseLabLook(blockType, look, arrangement.presentation),
  };
}

export function MatrixToolbar({
  state,
  onChange,
  showBlockAxes = true,
}: MatrixToolbarProps) {
  const themes = Object.values(THEME_REGISTRY);
  const colorsets = getColorSetsForTheme(state.themeId);
  const blockOptions = listLabBlockOptions();
  const arrangements = listLabArrangements(state.blockType);
  const looks = listLabLooks(state.blockType);

  return (
    <div className="lab-toolbar">
      {showBlockAxes ? (
        <>
          <div className="lab-field">
            <label htmlFor="lab-block">Block</label>
            <select
              id="lab-block"
              value={state.blockType}
              onChange={(e) => {
                const blockType = e.target.value as RegistryBlockType;
                const first = listLabArrangements(blockType)[0];
                if (!first) return;
                onChange(
                  patchFromArrangement(
                    blockType,
                    first,
                    parseLabLook(blockType, null, first.presentation),
                  ),
                );
              }}
            >
              {blockOptions.map((option) => (
                <option key={option.type} value={option.type}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="lab-field">
            <label htmlFor="lab-arrangement">Arrangement</label>
            <select
              id="lab-arrangement"
              value={state.arrangementId}
              onChange={(e) => {
                const arrangement = resolveLabArrangement(
                  state.blockType,
                  e.target.value,
                  undefined,
                );
                if (!arrangement) return;
                onChange(patchFromArrangement(state.blockType, arrangement, state.look));
              }}
            >
              {arrangements.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {looks ? (
            <div className="lab-field">
              <label htmlFor="lab-look">Look</label>
              <select
                id="lab-look"
                value={state.look ?? looks[0]?.value}
                onChange={(e) => onChange({ look: e.target.value })}
              >
                {looks.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </>
      ) : null}

      <div className="lab-field">
        <label htmlFor="lab-theme">Theme</label>
        <select
          id="lab-theme"
          value={state.themeId}
          onChange={(e) => {
            const themeId = e.target.value;
            const sets = getColorSetsForTheme(themeId);
            onChange({
              themeId,
              colorsetId: sets[0]?.id ?? state.colorsetId,
            });
          }}
        >
          {themes.map((t) => (
            <option key={t.themeId} value={t.themeId}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      <div className="lab-field">
        <label htmlFor="lab-colorset">Colorset</label>
        <select
          id="lab-colorset"
          value={state.colorsetId}
          onChange={(e) => onChange({ colorsetId: e.target.value })}
        >
          {colorsets.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="lab-field">
        <label htmlFor="lab-spacing">Spacing</label>
        <select
          id="lab-spacing"
          value={state.spacingEnum}
          onChange={(e) => onChange({ spacingEnum: e.target.value as SpacingEnumKey })}
        >
          {SPACING_ENUM_KEY_VALUES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="lab-field">
        <label htmlFor="lab-locale">Locale</label>
        <select
          id="lab-locale"
          value={state.locale}
          onChange={(e) => onChange({ locale: e.target.value as MatrixLocale })}
        >
          {MATRIX_LOCALES.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
      </div>

      <div className="lab-field">
        <label htmlFor="lab-viewport">Viewport</label>
        <select
          id="lab-viewport"
          value={state.viewport}
          onChange={(e) =>
            onChange({
              viewport: e.target.value as MatrixViewState['viewport'],
            })
          }
        >
          <option value="desktop">Desktop</option>
          <option value="tablet">Tablet</option>
          <option value="mobile">Mobile</option>
        </select>
      </div>
    </div>
  );
}
